import styled from '@emotion/styled';
import { HomeyAPI } from 'homey-api';
import { useEffect, useRef, useState } from 'react';

import { defer } from '../../../lib/defer';
import { SCOPES } from '../../../lib/scopes';
import { ToastManager } from '../../../ToastManager';
import { GeneralSettingsSection } from '../system/GeneralSettingsSection';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApi } from '../../../store/SelectedHomeyStore.hooks';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { icon } from '../../../theme/icon';
import { theme } from '../../../theme/theme';

import { GradientButton } from '../../../components/buttons/GradientButton';
import { Icon } from '../../../components/common/Icon';
import { Heading2Medium } from '../../../components/common/text/Heading2Medium';
import { ParagraphRegular } from '../../../components/common/text/ParagraphRegular';
import { ParagraphSmall } from '../../../components/common/text/ParagraphSmall';
import { Switch } from '../../../components/forms/Switch';

import { HeaderIcons } from './HeaderIcons';
export function GoogleAssistantSettings() {
  const { i18n } = useI18n();
  const { api, scopes } = useApi();

  const instanceRef = useRef({ pendingGet: null, pendingSet: null });
  const [isSyncEnabled, setIsSyncEnabled] = useState(false);

  useEffect(() => {
    if (api != null && HomeyAPI.isHomeyAPIV3Cloud(api) === false) {
      if (instanceRef.current.pendingGet == null) {
        (async () => {
          try {
            instanceRef.current.pendingGet = api.googleAssistant.getOptionEnabled();
            const option = await instanceRef.current.pendingGet;
            setIsSyncEnabled(option.value);
          } catch (error) {
            ToastManager.handleError(error);
          } finally {
            instanceRef.current.pendingGet = null;
          }
        })();
      }
    }
  }, [api]);

  return (
    <S.Root>
      <S.Content>
        <HeaderIcons>
          <Icon source={icon.apps.googleAssistant} size="48px" />
        </HeaderIcons>

        <Heading2Medium textAlign="center">Google Assistant</Heading2Medium>

        <S.ContentDescription textAlign="center" lineHeight={1.5} color={theme.color.text_light}>
          {i18n.messageFormatter('settings.integrations.googleAssistantSubtitle')}
        </S.ContentDescription>

        <GradientButton
          elementType="a"
          target="_blank"
          href="https://support.homey.app/hc/articles/360022832954"
        >
          <GradientButton.Icon
            source={glyph.core.squareArrowUpRight}
            color={theme.color.white}
            size={theme.icon.size_default}
          />
          <GradientButton.Text>
            {i18n.messageFormatter('settings.integrations.getStartedButtonTitle')}
          </GradientButton.Text>
        </GradientButton>
      </S.Content>

      {HomeyAPI.isHomeyAPIV3Cloud(api) === false && (
        <GeneralSettingsSection.S.Section>
          <GeneralSettingsSection.S.SectionView>
            <GeneralSettingsSection.S.Row>
              <GeneralSettingsSection.S.Label>
                {i18n.messageFormatter('settings.integrations.googleAssistantSyncLabel')}
              </GeneralSettingsSection.S.Label>
              <GeneralSettingsSection.S.Value>
                <Switch
                  aria-label="Sync"
                  name="sync"
                  isDisabled={scopes[SCOPES.SYSTEM] !== true}
                  value={isSyncEnabled}
                  onChange={(nextValue) => {
                    if (instanceRef.current.pendingSet != null) {
                      return;
                    }

                    const prevValue = isSyncEnabled;
                    setIsSyncEnabled(nextValue);

                    (async () => {
                      try {
                        const deferred = defer();
                        instanceRef.current.pendingSet = deferred.promise;

                        api.googleAssistant
                          .setOptionEnabled({
                            value: nextValue,
                          })
                          .then(deferred.resolve)
                          .catch(deferred.reject);

                        await instanceRef.current.pendingSet;
                      } catch (error) {
                        setIsSyncEnabled(prevValue);
                        ToastManager.handleError(error);
                      } finally {
                        instanceRef.current.pendingSet = null;
                      }
                    })();
                  }}
                />
              </GeneralSettingsSection.S.Value>
            </GeneralSettingsSection.S.Row>
            <S.Hint color={theme.color.text_light}>
              {i18n.messageFormatter('settings.integrations.googleAssistantSyncHint')}
            </S.Hint>
          </GeneralSettingsSection.S.SectionView>
        </GeneralSettingsSection.S.Section>
      )}
    </S.Root>
  );
}

GoogleAssistantSettings.pathKey = 'integrations/google-assistant';
GoogleAssistantSettings.icon = glyph.system.google;
GoogleAssistantSettings.backgroundColor = theme.color.system_google;

GoogleAssistantSettings.Root = styled.div`
  margin-bottom: ${su(2)};
  flex: 0 0 auto;
`;

function S() {}
GoogleAssistantSettings.S = S;

S.Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: ${su(4)};
  margin-bottom: ${su(2)};
  flex: 1 0 auto;
`;

S.Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(380px, 100%);

  &:not(:last-child) {
    margin-bottom: ${su(6)};
  }
`;

S.ContentDescription = styled(ParagraphRegular)`
  margin: ${su(2)} 0 ${su(4)};
`;

S.Hint = styled(ParagraphSmall)`
  margin: 5px 0 15px;
`;
